import React, { useState, useEffect } from "react";
import { Plus, X, Upload } from "lucide-react";
import type {
  Product,
  ProductFormData,
  ProductImage,
} from "../../../types/product";
import { productService } from "../../../services/productService";
import { categoryService } from "../../../services/categoryService";

interface ProductFormProps {
  initialData?: Partial<Product>;
  onSubmit: (data: ProductFormData) => Promise<void>;
  submitText?: string;
  isSubmitting?: boolean;
  onFileSelect?: (files: File[]) => Promise<void>;
}

interface Category {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
}

const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSubmit,
  submitText = "Save Product",
  isSubmitting = false,
  onFileSelect,
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    price: initialData?.price || 0,
    category: initialData?.category || "",
    brand: initialData?.brand || "",
    sku: initialData?.sku || "",
    stock: initialData?.stock || 0,
    featured: initialData?.featured || false,
    status: initialData?.status || "active",
    specifications: initialData?.specifications || {
      gsm: "",
      size: "",
      sheets: "",
      finish: "",
    },
  });

  const [files, setFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<ProductImage[]>(
    initialData?.images || []
  );
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    const fetchCategoriesAndBrands = async () => {
      try {
        setLoadingOptions(true);
        const [categoriesData, brandsData] = await Promise.all([
          categoryService.getAllCategories(),
          categoryService.getAllBrands(),
        ]);

        console.log("Fetched categories:", categoriesData);
        console.log("Fetched brands:", brandsData);

        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (err) {
        console.error("Error fetching categories and brands:", err);
        setError("Failed to load categories and brands");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchCategoriesAndBrands();
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (onFileSelect) {
      try {
        setIsUploading(true);
        await onFileSelect(selectedFiles);
        setFiles((prev) => [...prev, ...selectedFiles]);
      } catch (error) {
        console.error("Error uploading files:", error);
        setError("Failed to upload files");
      } finally {
        setIsUploading(false);
      }
    } else {
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const handleRemoveFile = (indexToRemove: number) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleRemoveExistingImage = (indexToRemove: number) => {
    setExistingImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.category || !formData.brand) {
      setError("Please fill in all required fields");
      return;
    }

    if (files.length === 0 && existingImages.length === 0) {
      setError("Please upload at least one product image");
      return;
    }

    setIsUploading(true);

    try {
      const uploadedImages: ProductImage[] = [];
      if (!onFileSelect) {
        for (const file of files) {
          const image = await productService.uploadProductImage(file);
          uploadedImages.push(image);
        }
      }

      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");

      const productFormData: ProductFormData = {
        ...formData,
        images: [...existingImages, ...uploadedImages],
        slug,
      };

      await onSubmit(productFormData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
            required
          />
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Price (KES)
          </label>
          <input
            type="number"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: Number(e.target.value) })
            }
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
          />
        </div>

        {/* Category Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.category}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, category: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
            required
            disabled={loadingOptions}
          >
            <option value="">Select Category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Brand Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Brand <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.brand}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, brand: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
            required
            disabled={loadingOptions}
          >
            <option value="">Select Brand</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.name}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        {/* Stock */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Stock <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={formData.stock}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                stock: Number(e.target.value),
              }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
            required
            min="0"
          />
        </div>

        {/* SKU */}
        <div>
          <label className="block text-sm font-medium text-gray-700">SKU</label>
          <input
            type="text"
            value={formData.sku}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sku: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
            rows={4}
          />
        </div>

        {/* Specifications */}
        <div className="md:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Specifications
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                GSM
              </label>
              <input
                type="text"
                value={formData.specifications.gsm}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specifications: {
                      ...prev.specifications,
                      gsm: e.target.value,
                    },
                  }))
                }
                className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Size
              </label>
              <input
                type="text"
                value={formData.specifications.size}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specifications: {
                      ...prev.specifications,
                      size: e.target.value,
                    },
                  }))
                }
                className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sheets
              </label>
              <input
                type="text"
                value={formData.specifications.sheets}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specifications: {
                      ...prev.specifications,
                      sheets: e.target.value,
                    },
                  }))
                }
                className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Finish
              </label>
              <input
                type="text"
                value={formData.specifications.finish}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specifications: {
                      ...prev.specifications,
                      finish: e.target.value,
                    },
                  }))
                }
                className="mt-1 w-full rounded-lg border p-2 focus:ring-2 focus:ring-brand-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Featured Product */}
        <div className="md:col-span-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, featured: e.target.checked }))
              }
              className="rounded border-gray-300 text-brand-blue-500 focus:ring-brand-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Featured Product
            </span>
          </label>
        </div>

        {/* Image Upload Section */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Images
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer rounded-md font-medium text-brand-blue-500 hover:text-brand-blue-600">
                  <span>Upload files</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="sr-only"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
            </div>
          </div>
        </div>

        {/* Preview Images */}
        {(files.length > 0 || existingImages.length > 0) && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {existingImages.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="h-24 w-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {files.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-24 w-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit and Cancel Buttons */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || isUploading || loadingOptions}
          className="px-4 py-2 text-white bg-brand-blue-500 rounded-lg hover:bg-brand-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting || isUploading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isUploading ? "Uploading..." : "Saving..."}
            </>
          ) : (
            submitText
          )}
        </button>
      </div>
    </form>
  );
};

export default ProductForm;
